import React from 'react';
import { Car, Route, Ambulance, Clock, Check, Heart, ShieldCheck, Mail, Calendar, HandHeart } from 'lucide-react';
import { services } from '../mock';

const iconMap = {
  car: Car,
  route: Route,
  ambulance: Ambulance,
  clock: Clock
};

const Services = () => {
  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
              Unsere Leistungen
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Professioneller Transport für Ihre Haustiere – sicher, zuverlässig und mit Herz
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="group relative bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:border-blue-400 hover:shadow-xl transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-orange-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>

                <div className="inline-block bg-orange-100 text-orange-700 px-4 py-2 rounded-full font-semibold text-lg mb-6">
                  {service.price}
                </div>

                <ul className="space-y-3">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>

                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 to-orange-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-gradient-to-r from-blue-600 to-orange-500 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Alle Haustiere willkommen!</h3>
          <p className="text-lg opacity-90 max-w-3xl mx-auto">
            Ob Hund, Katze, Kaninchen, Vogel oder exotisches Tier – wir transportieren alle Ihre Lieblinge sicher und artgerecht. Kontaktieren Sie uns für individuelle Anfragen!
          </p>
        </div>

        {/* Kostenlose Nothilfe fuer verletzte Fundtiere */}
        <div
          className="mt-10 relative overflow-hidden rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 via-white to-orange-50 p-8 md:p-10 shadow-lg"
          data-testid="fundtier-nothilfe-section"
        >
          {/* dezenter Herz-Hintergrund */}
          <Heart className="absolute -right-10 -bottom-10 h-64 w-64 text-red-100 rotate-12 pointer-events-none" strokeWidth={1} />

          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-14 h-14 rounded-2xl bg-red-500 flex items-center justify-center shadow-md">
                <HandHeart className="h-7 w-7 text-white" />
              </div>
              <div>
                <span className="inline-block bg-red-100 text-red-700 text-xs font-semibold px-3 py-1 rounded-full mb-1">
                  Ehrenamtliche Nothilfe
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Kostenlose Nothilfe für verletzte Fundtiere
                </h3>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6 max-w-3xl">
              Verletzte Fundtiere brauchen schnelle Hilfe – und die soll nicht am Transport scheitern.
              Aus diesem Grund übernehme ich unter bestimmten Voraussetzungen <strong>kostenlose Notfallfahrten</strong> für
              verletzte Fundtiere zur nächsten aufnahmebereiten Tierklinik.
            </p>

            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
              Voraussetzungen
            </h4>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <div className="flex items-start gap-3 bg-white/70 backdrop-blur-sm border border-red-100 rounded-lg p-3">
                <ShieldCheck className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">
                  Es handelt sich um ein verletztes Fundtier, das <strong>akut medizinische Hilfe</strong> benötigt.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-white/70 backdrop-blur-sm border border-red-100 rounded-lg p-3">
                <Mail className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">
                  Die Behandlung ist mit der Tierklinik im Vorfeld abgeklärt – inkl. <strong>schriftlicher Zusage per E-Mail</strong>,
                  dass das Tier aufgenommen und die Kosten von der Klinik oder Ihnen getragen werden.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-white/70 backdrop-blur-sm border border-red-100 rounded-lg p-3">
                <Calendar className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">
                  Die Fahrt <strong>kollidiert nicht mit bereits gebuchten Terminen</strong>.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-white/70 backdrop-blur-sm border border-red-100 rounded-lg p-3">
                <Heart className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">
                  Aus Kostengründen sind aktuell <strong>maximal 2 solcher Transporte pro Monat</strong> möglich.
                </span>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm border border-red-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-gray-700">
                Ein Fundtier gefunden und Klinik-Zusage vorhanden?<br className="hidden sm:block" />
                <span className="text-gray-600">Melden Sie sich direkt telefonisch – jede Minute zählt.</span>
              </div>
              <a
                href="tel:+4915739696942"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-3 rounded-lg transition-colors shadow-md whitespace-nowrap"
                data-testid="fundtier-nothilfe-call-btn"
              >
                <Ambulance className="h-4 w-4" />
                Jetzt Notfall melden
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
